/* ============================================================
   Finish comparison — interactive 3D finish spheres
   ------------------------------------------------------------
   Ported from the approved single-file prototype (finish-spinner-3d.html).
   • One WebGL context per section; each ball is drawn into its own
     scissored viewport aligned to its DOM stage (.finish-swatch-media).
   • Lighting is a procedural PMREM "studio": softbox planes + a gradient
     dome. Each finish selects its own rig from ENV_SCENES, because the flat
     product references are lit differently per material.
   • Interaction: drag / swipe to spin (with inertia), arrow keys when a
     ball is focused, gentle idle drift around a tilted axis.
     prefers-reduced-motion disables idle drift and inertia.
   • No WebGL → the static swatch gradients simply stay visible.
   • This file is only loaded when the section is in 3D mode; flat mode
     renders the pre-rendered PNGs and loads no JS at all.
   • Glass and Paintable were added from finish-spinner-3d_2.html: glass is
     real refraction (transmission) over a hidden backdrop matched to the
     section background, plus a tinted rim, seed bubbles and an internal
     glow; paintable is an eggshell ball with Corston-green paint poured over
     the top of its spin axis.
   ============================================================ */
(() => {
  'use strict';
  if (window.corstonFinishSpinnerInit) return;
  window.corstonFinishSpinnerInit = true;

  const reducedMotion = matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ---------------- Finish definitions (tuned in the prototype) ----------------
     `env` selects the per-finish lighting rig from ENV_SCENES below. */
  const FINISHES = {
    'antique-brass': {   // pale creamy gold: soft warm bloom + fine grain mottle
      color:'#d2bd7e', metalness:0.92, roughness:0.44, envInt:0.92, env:'warmSoft',
      anisotropy:0.0, clearcoat:0.0, clearcoatRoughness:0.0,
      tex:{ kind:'patina', strength:0.63, scale:1.2, tint:'#93825a', grain:1.17 }
    },
    'bronze': {          // velvet-matte dark taupe: one huge diffuse key, smooth falloff
      color:'#5d5650', metalness:0.82, roughness:0.72, envInt:0.92, env:'matteGrad',
      anisotropy:0.15, anisotropyRotation:0.0, clearcoat:0.08, clearcoatRoughness:0.6,
      tex:{ kind:'brushed', strength:0.227, scale:2.5, dirJitter:0.04, curve:0.02, length:1.0, varied:true, base:0.72, fine:true, relief:0.5 }
    },
    'polished-nickel': { // mirror chrome: crisp boxes, dark horizon, floor bounce below
      color:'#dcdad6', metalness:1.0, roughness:0.008, envInt:1.15, env:'mirror2',
      anisotropy:0.0, clearcoat:0.0, clearcoatRoughness:0.0,
      tex:{ kind:'polish', waviness:0.45, dust:0.4, smudge:0.3, scratches:0.3 }
    },
    'glass': {           // real refraction (transmission) + sea-glass body tint;
                         // seed bubbles + internal glow make it legible & spinnable
      glass:true, color:'#ffffff', roughness:0.03, envInt:1.0, env:'glassStudio',
      ior:1.5, thickness:1.4, attenuationColor:'#c4d6d0', attenuationDistance:4.5, dispersion:0,
      rimColor:'#6c827c', rimStart:0.42, rimAmount:0.72,
      bubbles:11, glow:0.17, backdropShade:0.94, backdropGain:1.18,
      tex:{ kind:'none' }
    },
    'paintable': {       // eggshell grey + one Corston-green sample brush stroke (colour/rough/bump maps)
      color:'#ffffff', metalness:0.0, roughness:0.5, envInt:1.0, env:'paintSoft', heading:0,
      anisotropy:0.0, clearcoat:0.0, clearcoatRoughness:0.0,
      tex:{ kind:'pour', base:'#ddd9d2', paint:'#5a634c', paintExposure:0.74, baseRough:0.5, paintRough:0.46 }  // paint = Corston green; kind:'paint' = the old brush stroke
    },
    'satin-nickel': {    // brushed warm silver — dense circumferential lines (lathe-brushed),
                         // tightly stacked, running horizontally around the sphere
      color:'#c8c2b7', metalness:0.96, roughness:0.44, envInt:1.35, env:'satinSoft2',
      anisotropy:0.0, anisotropyRotation:0.0, clearcoat:0.0, clearcoatRoughness:0.0,
      // short indented strokes: ~12-32px segments (≈2-4mm), near-zero gap, groove
      // core + lit ridge in the bump map. NOTE: anisotropy must stay 0 — three.js's
      // anisotropic IBL path all but cancels bump relief (found during tuning).
      tex:{ kind:'lathe', seg:true, strength:2.76, pitch:1.2, wobble:6, segMin:12, segMax:32, gap:2,
            depth:1.2, ridge:0.9, relief:18, accents:0.8, base:0.52 }
    }
  };

  function initSection(section){
    const canvas = section.querySelector('.finish-comparison-gl');
    if (!canvas || !window.THREE) return;
    const T = window.THREE;

    /* ---------------- Renderer ---------------- */
    let renderer;
    try {
      renderer = new T.WebGLRenderer({ canvas, alpha:true, antialias:true, powerPreference:'high-performance' });
    } catch (e) { return; }
    renderer.outputColorSpace = T.SRGBColorSpace;
    renderer.toneMapping = T.ACESFilmicToneMapping;
    renderer.toneMappingExposure = 1.05;
    renderer.setClearColor(0x000000, 0);
    const DPR = () => Math.min(window.devicePixelRatio || 1, 2);

    /* ---------------- Procedural studio environments ---------------- */
    const pmrem = new T.PMREMGenerator(renderer);

    function gradientDome(stops){
      const c = document.createElement('canvas'); c.width = 4; c.height = 512;
      const g = c.getContext('2d').createLinearGradient(0,0,0,512);
      stops.forEach(([off,col]) => g.addColorStop(off,col));
      const ctx = c.getContext('2d'); ctx.fillStyle = g; ctx.fillRect(0,0,4,512);
      const tex = new T.CanvasTexture(c); tex.colorSpace = T.SRGBColorSpace;
      const m = new T.MeshBasicMaterial({ map:tex, side:T.BackSide });
      return new T.Mesh(new T.SphereGeometry(50,24,32), m);
    }
    function softboxTexture(soft){
      const c = document.createElement('canvas'); c.width = c.height = 256;
      const x = c.getContext('2d');
      x.filter = `blur(${Math.round(10 + soft*26)}px)`;
      x.fillStyle = '#fff';
      const m = 34 + soft*18, r = 26;
      x.beginPath(); x.roundRect(m, m, 256-2*m, 256-2*m, r); x.fill();
      return new T.CanvasTexture(c);
    }
    function softbox(w, h, pos, color, intensity, soft=0.5){
      const mat = new T.MeshBasicMaterial({ map:softboxTexture(soft), transparent:true, side:T.DoubleSide });
      mat.color = new T.Color(color).multiplyScalar(intensity);
      const mesh = new T.Mesh(new T.PlaneGeometry(w,h), mat);
      mesh.position.set(...pos); mesh.lookAt(0,0,0);
      return mesh;
    }

    function roundLight(d, pos, color, intensity, soft=0.4){
      const c = document.createElement('canvas'); c.width = c.height = 256;
      const x = c.getContext('2d');
      const g = x.createRadialGradient(128,128,0,128,128,128);
      g.addColorStop(0,'#fff'); g.addColorStop(Math.max(0.05, 0.62 - soft*0.5),'#fff'); g.addColorStop(1,'rgba(255,255,255,0)');
      x.fillStyle = g; x.fillRect(0,0,256,256);
      const mat = new T.MeshBasicMaterial({ map:new T.CanvasTexture(c), transparent:true, side:T.DoubleSide });
      mat.color = new T.Color(color).multiplyScalar(intensity);
      const mesh = new T.Mesh(new T.PlaneGeometry(d,d), mat);
      mesh.position.set(...pos); mesh.lookAt(0,0,0);
      return mesh;
    }

    /* Per-finish studio rigs. The flat product references are lit differently
       per material (soft warm sources on brass, one huge diffuse key on bronze,
       crisp boxes + bulb on chrome, a broad wrap on satin), so each finish
       selects its own via its `env` key.
       Map values are [sceneBuilder, pmremBlurSigma]. */
    const ENV_SCENES = {
      /* shared default: softboxes above, product-table falloff below */
      studio: [() => {
        const s = new T.Scene();
        s.add(gradientDome([[0,'#fbf8f1'],[0.40,'#ebe6db'],[0.54,'#b8b1a5'],[0.68,'#847c72'],[0.84,'#5d564d'],[1,'#4d463e']]));
        s.add(softbox(3.4,2.6,[-2.1,2.6,2.5],'#fff6e8',17,0.45));
        s.add(softbox(2.0,1.6,[ 2.4,2.2,2.2],'#f6f7f8',9,0.5));
        s.add(softbox(0.55,0.55,[0.5,2.9,2.9],'#ffffff',13,0.3));
        s.add(softbox(4.0,0.5,[0,3.5,-0.4],'#fffaf2',6,0.4));
        return s; }, 0.035],
      /* antique brass: big warm soft sources, small hotspot, gentle olive floor */
      warmSoft: [() => {
        const s = new T.Scene();
        s.add(gradientDome([[0,'#fdfaf3'],[0.42,'#efe9dc'],[0.58,'#c0b8a5'],[0.75,'#837a66'],[1,'#5f5745']]));
        s.add(softbox(3.6,2.8,[-2.0,2.0,2.5],'#fff4e0',13,0.7));   // warm key
        s.add(softbox(2.6,2.2,[ 2.5,1.4,2.2],'#f6f1e6',6,0.75));   // soft fill
        s.add(softbox(0.45,0.45,[-0.2,1.9,3.0],'#ffffff',9,0.35)); // small hotspot
        return s; }, 0.04],
      /* bronze: one huge blurred key over a falling gradient — velvet matte */
      matteGrad: [() => {
        const s = new T.Scene();
        s.add(gradientDome([[0,'#efeae2'],[0.45,'#c9c2b6'],[0.65,'#847b6e'],[1,'#5a534b']]));
        s.add(softbox(4.2,3.2,[-2.8,1.8,2.0],'#fdf6ea',8,0.85));
        return s; }, 0.04],
      /* polished nickel: crisp box + pill + bare bulb, dark horizon band,
         then a lighter floor bounce at the very bottom (as in the flat shot) */
      mirror2: [() => {
        const s = new T.Scene();
        s.add(gradientDome([[0,'#f2f0ec'],[0.36,'#e2ddd3'],[0.50,'#c6c1b6'],[0.68,'#6b645c'],[0.74,'#4a453e'],[0.79,'#8a847b'],[1,'#aaa49a']]));
        s.add(softbox(3.8,3.4,[-1.5,1.8,2.4],'#fffdf7',22,0.12));  // key box, left of centre
        s.add(softbox(2.4,1.9,[ 2.0,1.5,2.2],'#fafbfc',16,0.28));  // pill, right
        s.add(softbox(0.5,0.5,[0.15,1.55,3.0],'#ffffff',24,0.1));  // bare bulb
        return s; }, 0.02],
      /* glass: one round soft key for the highlight, cool sea-glass horizon so
         the Fresnel rim picks up the teal-grey edge from the mock */
      glassStudio: [() => {
        const s = new T.Scene();
        s.add(gradientDome([[0,'#f8f9f7'],[0.40,'#e2e7e4'],[0.54,'#a9b6b2'],[0.68,'#74837f'],[0.85,'#5f6d69'],[1,'#6d7a76']]));
        s.add(roundLight(3.1,[-2.3,2.8,1.9],'#ffffff',120,0.5));  // the highlight (glass reflects ~4%)
        s.add(softbox(2.4,1.8,[ 2.6,1.0,1.6],'#f3f6f5',3,0.8));     // faint fill right
        return s; }, 0.02],
      /* paintable: eggshell paint — neutral soft wrap, one round soft key */
      paintSoft: [() => {
        const s = new T.Scene();
        s.add(gradientDome([[0,'#8c8882'],[0.42,'#7d7973'],[0.60,'#66625c'],[0.80,'#4d4a45'],[1,'#403d39']]));
        s.add(roundLight(3.0,[-1.3,1.7,3.1],'#fffdf8',26,0.8));    // big soft key, near-frontal → all edges fall off
        s.add(softbox(2.6,2.2,[ 2.6,1.0,2.0],'#f5f3ef',2.5,0.85)); // dim fill
        return s; }, 0.035],
      /* satin nickel: broad soft key, dim fill, darker floor — a big light/dark
         ratio is what makes brushed metal read as metal */
      satinSoft2: [() => {
        const s = new T.Scene();
        s.add(gradientDome([[0,'#f9f6ef'],[0.42,'#e2ddd2'],[0.6,'#aaa397'],[0.8,'#6b6458'],[1,'#4d483f']]));
        s.add(softbox(3.4,2.6,[-1.9,1.9,2.5],'#fdfaf2',15,0.75));
        s.add(softbox(2.4,2.0,[ 2.4,1.3,2.2],'#f4f2ec',3.5,0.85));
        s.add(softbox(3.4,0.5,[0,3.3,0.2],'#fdf8ee',4,0.5));
        return s; }, 0.04]
    };
    let ENV_CACHE = {};
    function getEnv(name){
      const key = ENV_SCENES[name] ? name : 'studio';
      if (!ENV_CACHE[key]){
        const [make, sigma] = ENV_SCENES[key];
        ENV_CACHE[key] = pmrem.fromScene(make(), sigma).texture;
      }
      return ENV_CACHE[key];
    }

    /* ---------------- Procedural finish detail textures ---------------- */
    const TEXW = 1024, TEXH = 512;
    function makeCanvas(){ const c = document.createElement('canvas'); c.width = TEXW; c.height = TEXH; return c; }
    function wrapDot(x, cx, cy, r, fill){ // draw with horizontal wrap
      for (const ox of [-TEXW, 0, TEXW]){
        const g = x.createRadialGradient(cx+ox, cy, 0, cx+ox, cy, r);
        g.addColorStop(0, fill); g.addColorStop(1, 'rgba(0,0,0,0)');
        x.fillStyle = g; x.fillRect(cx+ox-r, cy-r, r*2, r*2);
      }
    }
    let seed = 1234;
    const rnd = () => (seed = (seed * 16807) % 2147483647) / 2147483647;

    /* Returns {map?, roughnessMap?, bumpMap?, bumpScale?} — roughness encoded so
       material.roughness acts as the MAX and the map modulates around params. */
    function makeDetailTextures(p){
      const t = p.tex || { kind:'none' };
      const S = t.strength ?? 0.5;
      const out = {};
      seed = 1234;

      if (t.kind === 'brushed'){
        // fine brush scratches; dirJitter: 0 = all one direction … 1 = fully random.
        // base: mid roughness level of the map (0–1); scratches: sparse long hairlines.
        const c = makeCanvas(), x = c.getContext('2d');
        const baseFill = Math.round(255 * (t.base ?? 0.5));
        x.fillStyle = `rgb(${baseFill},${baseFill},${baseFill})`; x.fillRect(0,0,TEXW,TEXH);
        const n = Math.round(2600 * (t.scale ?? 1));
        const jit = (t.dirJitter ?? 0.85) * Math.PI;
        const curve = t.curve ?? 0.6;
        const lenMul = t.length ?? 1;          // stroke length multiplier (sweeping arcs)
        // varied: skewed length distribution — mostly short strokes, occasional long ones
        const strokeLen = () => (t.varied ? (10 + Math.pow(rnd(), 2) * 260) : (14 + rnd()*90)) * lenMul;
        // flow mode: strokes align tangentially around swirl centres, like a
        // machine-polished satin finish (locally coherent fibres, not crackle)
        const centers = t.flow
          ? Array.from({length: 7}, () => [rnd()*TEXW, (rnd()*1.6 - 0.3)*TEXH])
          : null;
        for (let i=0;i<n;i++){
          const y0 = rnd()*TEXH, x0 = rnd()*TEXW, len = strokeLen();
          let ang, bend;
          if (centers){
            let bd = 1e18, bx = 0, by = 0;
            for (const cc of centers){
              for (const ox of [-TEXW, 0, TEXW]){
                const dx = x0-(cc[0]+ox), dy = y0-cc[1], dd = dx*dx+dy*dy;
                if (dd < bd){ bd = dd; bx = cc[0]+ox; by = cc[1]; }
              }
            }
            const dist = Math.sqrt(bd) + 1;
            ang = Math.atan2(y0-by, x0-bx) + Math.PI/2 + (rnd()-0.5) * jit * 0.25;
            bend = Math.min(len*0.4, (len*len) / (2.4*dist));   // arc around the centre
          } else {
            ang = (rnd()-0.5) * jit;
            bend = (rnd()-0.5) * curve * len;
          }
          const a = (0.03 + rnd()*0.12) * S * 2;
          x.strokeStyle = rnd() > 0.5 ? `rgba(255,255,255,${a})` : `rgba(0,0,0,${a})`;
          const lw = rnd() < 0.85 ? 1 : 2;
          x.lineWidth = t.fine ? 1 : lw;       // fine: hairline strokes only
          const ca = Math.cos(ang), sa = Math.sin(ang);
          for (const ox of [-TEXW,0,TEXW]){
            x.beginPath();
            x.moveTo(x0+ox, y0);
            x.quadraticCurveTo(x0+ox + ca*len/2 - sa*bend, y0 + sa*len/2 + ca*bend,
                               x0+ox + ca*len, y0 + sa*len);
            x.stroke();
          }
        }
        const nScr = Math.round((t.scratches ?? 0) * 2 * 9);
        for (let i=0;i<nScr;i++){   // sparse long hairlines at random angles
          const y0 = rnd()*TEXH, x0 = rnd()*TEXW, len = 80 + rnd()*300;
          const ang = (rnd()-0.5) * Math.PI, a = 0.10 + rnd()*0.22;
          x.strokeStyle = `rgba(255,255,255,${a})`; x.lineWidth = 1;
          const ca = Math.cos(ang), sa = Math.sin(ang);
          for (const ox of [-TEXW,0,TEXW]){
            x.beginPath(); x.moveTo(x0+ox, y0);
            x.quadraticCurveTo(x0+ox + ca*len/2, y0 + sa*len/2 + (rnd()-0.5)*10,
                               x0+ox + ca*len, y0 + sa*len);
            x.stroke();
          }
        }
        out.roughnessMap = c; out.bumpMap = c;
        out.bumpScale = 0.0035 * S * (t.relief ?? 1);   // relief decoupled from contrast
      }

      if (t.kind === 'lathe'){
        // circumferential brushing: hairlines at near-regular pitch wrapping
        // horizontally around the sphere. Default: continuous lines.
        // seg:true breaks them into short individual strokes (a few mm each) and
        // builds groove relief (dark core + bright ridge) on a separate bump
        // canvas so each stroke reads as indented.
        const c = makeCanvas(), x = c.getContext('2d');
        const baseFill = Math.round(255 * (t.base ?? 0.5));
        x.fillStyle = `rgb(${baseFill},${baseFill},${baseFill})`; x.fillRect(0,0,TEXW,TEXH);
        const cb = makeCanvas(), xb = cb.getContext('2d');
        xb.fillStyle = '#808080'; xb.fillRect(0,0,TEXW,TEXH);
        const pitch = t.pitch ?? 1.6, wob = t.wobble ?? 0.7;

        if (t.seg){
          const sMin = t.segMin ?? 26, sMax = t.segMax ?? 64;
          const gap = t.gap ?? 8, depth = t.depth ?? 0.6, ridge = t.ridge ?? 0.5;
          for (let y = 1; y < TEXH; y += 0.8 + rnd()*pitch){
            let px = -rnd() * sMax;
            while (px < TEXW){
              const len = sMin + rnd()*(sMax - sMin);
              const dy = (rnd()-0.5) * wob, slope = (rnd()-0.5) * 1.2;
              const a = (0.05 + rnd()*0.16) * S;
              x.strokeStyle = rnd() > 0.35 ? `rgba(255,255,255,${a})` : `rgba(0,0,0,${a})`;
              x.lineWidth = 1;
              for (const ox of [-TEXW, 0, TEXW]){
                x.beginPath(); x.moveTo(px+ox, y+dy); x.lineTo(px+ox+len, y+dy+slope); x.stroke();
              }
              const da = (0.25 + rnd()*0.5) * depth;
              xb.strokeStyle = `rgba(0,0,0,${da})`; xb.lineWidth = 1;   // groove core
              for (const ox of [-TEXW, 0, TEXW]){
                xb.beginPath(); xb.moveTo(px+ox, y+dy); xb.lineTo(px+ox+len, y+dy+slope); xb.stroke();
              }
              xb.strokeStyle = `rgba(255,255,255,${da * ridge})`;       // bright ridge below
              for (const ox of [-TEXW, 0, TEXW]){
                xb.beginPath(); xb.moveTo(px+ox, y+dy+1); xb.lineTo(px+ox+len, y+dy+slope+1); xb.stroke();
              }
              px += len + gap * (0.3 + rnd());
            }
          }
        } else {
          for (let y = 0; y < TEXH; y += 0.8 + rnd()*pitch){
            const a = (0.05 + rnd()*0.15) * S;
            x.strokeStyle = rnd() > 0.5 ? `rgba(255,255,255,${a})` : `rgba(0,0,0,${a})`;
            x.lineWidth = 1;
            const n = 1 + Math.floor(rnd()*3), ph = rnd()*Math.PI*2, amp = rnd()*wob;
            x.beginPath(); x.moveTo(0, y + Math.sin(ph)*amp);
            for (let px = 32; px <= TEXW; px += 32)
              x.lineTo(px, y + Math.sin(px/TEXW*Math.PI*2*n + ph)*amp);   // seamless wrap
            x.stroke();
          }
        }
        const acc = Math.round((t.accents ?? 0.5) * 2 * 160);   // sparse stronger hairlines
        for (let i=0;i<acc;i++){
          const y = rnd()*TEXH, x0 = rnd()*TEXW, len = 120 + rnd()*420;
          const a = 0.08 + rnd()*0.18, dy = (rnd()-0.5)*1.5;
          x.strokeStyle = rnd() > 0.4 ? `rgba(255,255,255,${a})` : `rgba(0,0,0,${a})`;
          x.lineWidth = 1;
          for (const ox of [-TEXW,0,TEXW]){
            x.beginPath(); x.moveTo(x0+ox, y); x.lineTo(x0+ox+len, y+dy); x.stroke();
          }
        }
        out.roughnessMap = c;
        out.bumpMap = t.seg ? cb : c;
        out.bumpScale = 0.0035 * S * (t.relief ?? 0.5);
      }

      if (t.kind === 'pour'){
        // A thick band of paint poured over the top of the ball's spin axis,
        // dripping at both ends; computed per texel straight onto the sphere texture.
        // Everything is a signed distance in those screen units, so edges are
        // crisp + anti-aliased, and the bump gets a rounded, thick-paint profile.
        const W = 2048, H = 1024;
        const mk = () => { const c = document.createElement('canvas'); c.width = W; c.height = H; return c; };
        const col = mk(), cx = col.getContext('2d');
        const rgh = mk(), rx = rgh.getContext('2d');
        const bmp = mk(), bx = bmp.getContext('2d');
        cx.fillStyle = t.base || '#ddd9d2'; cx.fillRect(0,0,W,H);
        const rb = Math.round(255 * (t.baseRough ?? 0.5)), rp = Math.round(255 * (t.paintRough ?? 0.3));
        rx.fillStyle = `rgb(${rb},${rb},${rb})`; rx.fillRect(0,0,W,H);
        bx.fillStyle = '#808080'; bx.fillRect(0,0,W,H);
        for (let i=0;i<9000;i++){                      // faint eggshell "orange peel" on the base
          bx.fillStyle = rnd() > 0.5 ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)';
          bx.beginPath(); bx.arc(rnd()*W, rnd()*H, 1 + rnd()*2.5, 0, 7); bx.fill();
        }
        const [pr, pg, pb] = [1,3,5].map(i => parseInt((t.paint || '#5a634c').slice(i,i+2), 16));
        const ex = t.paintExposure ?? 1, PR = pr*ex, PG = pg*ex, PB = pb*ex;
        const [br, bgc, bb] = [1,3,5].map(i => parseInt((t.base || '#ddd9d2').slice(i,i+2), 16));

        // ---- the pour: a thick band of paint over the top of the SPIN AXIS ----
        // y = the spin axis (the ball's pole), so the band passes over the point
        // where the axis leaves the ball and turns around it. The band runs down
        // the front AND the back; both ends finish in a scalloped edge with a
        // pull-back notch and drips. x = across the band, depth = along it
        // (front faces the camera at rest). Sphere radius = 1.
        const cL = t.left ?? 0.45;                                   // band edges (across)
        const cR = (sy) => (t.right ?? 0.5) - 0.09 * Math.max(0, sy - 0.2) / 0.7;   // one edge curls in toward the top
        const notch = (x, nx) => 0.21 * Math.exp(-Math.pow((x - nx) / 0.055, 2));   // rounded pull-back
        const ends = [
          { // front end
            edge: (x) => (t.level ?? 0.13) + 0.055*Math.sin(x*7 + 1.0) + 0.022*Math.sin(x*15 + 2.0) + notch(x, 0.10),
            drips: [[-0.37, 0.10, -0.40], [-0.17, 0.085, 0.00], [0.29, 0.09, 0.02], [0.47, 0.08, -0.03]] },
          { // back end: its own scallops, notch and drips
            edge: (x) => (t.level ?? 0.13) + 0.02 + 0.05*Math.sin(x*6.3 + 2.6) + 0.02*Math.sin(x*13 + 0.7) + notch(x, -0.20),
            drips: [[-0.40, 0.085, -0.12], [-0.02, 0.10, -0.36], [0.22, 0.08, 0.01], [0.44, 0.09, -0.24]] }
        ];
        const smax = (a, b, k) => { const h = Math.max(k - Math.abs(a - b), 0) / k; return Math.max(a, b) + h * h * k * 0.25; };
        const sdf = (sx, sy, depth) => {
          const end = ends[depth >= 0 ? 0 : 1];        // the two ends only meet over the top, where the band is solid
          let d = Math.min(sx + cL + 0.006*Math.sin(sy*9), cR(sy) - sx, sy - end.edge(sx));
          // each drip = a capsule hanging from inside the paint + a slightly fatter
          // drop at its tip, blended in with a smooth union → liquid-looking fillets
          for (const [dx, w, tip] of end.drips){
            const top = end.edge(dx) + 0.06, bot = tip + w * 0.5;
            const xc = dx + 0.004 * Math.sin(sy * 20);
            const yy = Math.min(top, Math.max(bot, sy));
            const dc = w * 0.42 - Math.hypot(sx - xc, sy - yy);
            const db = w * 0.55 - Math.hypot(sx - dx, sy - bot);
            d = smax(d, smax(dc, db, 0.03), 0.07);
          }
          return d;
        };
        const tipBonus = (sx, sy, depth) => {
          let m = 0;
          for (const [dx, w, tip] of ends[depth >= 0 ? 0 : 1].drips){
            const by = tip + w*0.5; m = Math.max(m, 1 - Math.hypot(sx-dx, sy-by) / (w*0.55));
          }
          return Math.max(0, m);
        };
        // frame: up = local pole; front = the horizontal direction that faces the camera at rest
        const q0 = new T.Quaternion().setFromAxisAngle(new T.Vector3(0,1,0), p.heading || 0).premultiply(TILT);
        const camL = new T.Vector3(0, 0, 1).applyQuaternion(q0.clone().invert());
        const F = new T.Vector3(camL.x, 0, camL.z).normalize();
        const Rt = new T.Vector3().crossVectors(new T.Vector3(0,1,0), F);   // 'right' when facing front
        const C = cx.getImageData(0,0,W,H), Rg = rx.getImageData(0,0,W,H), B = bx.getImageData(0,0,W,H);
        const cd = C.data, rd = Rg.data, bd = B.data;
        const cosP = new Float32Array(W), sinP = new Float32Array(W);
        for (let c = 0; c < W; c++){ const ph = (c + 0.5) / W * Math.PI * 2; cosP[c] = Math.cos(ph); sinP[c] = Math.sin(ph); }
        const AA = 0.0028;                                            // ~1 texel in screen units
        for (let r = 0; r < H; r++){
          const th = (r + 0.5) / H * Math.PI, st = Math.sin(th), ct = Math.cos(th);
          for (let c = 0; c < W; c++){
            // three.js SphereGeometry: x = -cos(φ)sinθ, y = cosθ, z = sin(φ)sinθ  (row 0 = top)
            const lx = -cosP[c]*st, ly = ct, lz = sinP[c]*st;
            const wx = Rt.x*lx + Rt.z*lz, wy = ly, wz = F.x*lx + F.z*lz;   // spin-axis frame
            const d = sdf(wx, wy, wz);
            if (d < -AA) continue;
            const a = Math.min(1, (d + AA) / (2*AA));                 // coverage (anti-aliased edge)
            const i = (r * W + c) * 4;
            cd[i] = br + (PR - br) * a; cd[i+1] = bgc + (PG - bgc) * a; cd[i+2] = bb + (PB - bb) * a;
            rd[i] = rd[i+1] = rd[i+2] = rb + (rp - rb) * a;
            // thick-paint profile: rounded ramp up from the edge, drop tips sit higher
            const hgt = Math.min(1, Math.max(0, d / 0.035));
            const h = 128 + a * 12 + (hgt * hgt * (3 - 2*hgt)) * 46 + tipBonus(wx, wy, wz) * 30 * a;
            bd[i] = bd[i+1] = bd[i+2] = Math.min(255, h);
          }
        }
        cx.putImageData(C,0,0); rx.putImageData(Rg,0,0); bx.putImageData(B,0,0);
        out.map = col; out.roughnessMap = rgh; out.absoluteRoughness = true;
        out.bumpMap = bmp; out.bumpScale = 0.16 * (t.relief ?? 1);
      }

      if (t.kind === 'polish'){
        // real polished-plate character (from sample photos): mirror base +
        // polish waviness (makes reflections swim while spinning), dust nibs,
        // broad wipe smudges, sparse hairline scratches.
        // Roughness here is ABSOLUTE: the map carries real roughness values
        // (base = p.roughness), so smudges can be several × the mirror base.
        const rough = makeCanvas(), rx = rough.getContext('2d');
        const baseV = Math.round(255 * Math.min(1, p.roughness ?? 0.03));
        rx.fillStyle = `rgb(${baseV},${baseV},${baseV})`; rx.fillRect(0,0,TEXW,TEXH);
        const bump = makeCanvas(), bx = bump.getContext('2d');
        bx.fillStyle = '#808080'; bx.fillRect(0,0,TEXW,TEXH);

        const smudge = t.smudge ?? 0.5;
        for (let i=0;i<Math.round(smudge*2*6);i++){          // wipe bands (roughness only)
          const y0 = rnd()*TEXH, x0 = rnd()*TEXW, len = 200 + rnd()*500;
          const ang = (rnd()-0.5)*0.9, w = 30 + rnd()*70;
          const a = (0.05 + rnd()*0.11) * smudge * 2;
          rx.strokeStyle = `rgba(255,255,255,${a})`; rx.lineCap = 'round'; rx.lineWidth = w;
          const ca = Math.cos(ang), sa = Math.sin(ang);
          for (const ox of [-TEXW,0,TEXW]){
            rx.beginPath(); rx.moveTo(x0+ox, y0);
            rx.quadraticCurveTo(x0+ox + ca*len/2, y0 + sa*len/2 + (rnd()-0.5)*40,
                                x0+ox + ca*len, y0 + sa*len);
            rx.stroke();
          }
        }
        const wav = t.waviness ?? 0.5;
        for (let i=0;i<16;i++){                              // waviness (bump only)
          const r = 90 + rnd()*180, a = (0.10 + rnd()*0.12) * wav * 2;
          wrapDot(bx, rnd()*TEXW, rnd()*TEXH, r,
            (rnd() > 0.5 ? `rgba(255,255,255,${a})` : `rgba(0,0,0,${a})`));
        }
        const dust = t.dust ?? 0.5;
        for (let i=0;i<Math.round(dust*2*150);i++){          // dust nibs (bump + rough dots)
          const x0 = rnd()*TEXW, y0 = rnd()*TEXH, r = 0.5 + rnd()*1.4;
          bx.fillStyle = `rgba(255,255,255,${0.35 + rnd()*0.4})`;
          bx.beginPath(); bx.arc(x0, y0, r, 0, 7); bx.fill();
          rx.fillStyle = `rgba(255,255,255,${0.22 + rnd()*0.3})`;
          rx.beginPath(); rx.arc(x0, y0, r*1.3, 0, 7); rx.fill();
        }
        const scr = t.scratches ?? 0.5;
        for (let i=0;i<Math.round(scr*2*9);i++){             // hairlines (bump + rough)
          const y0 = rnd()*TEXH, x0 = rnd()*TEXW, len = 60 + rnd()*260;
          const ang = (rnd()-0.5)*Math.PI, a = 0.12 + rnd()*0.25;
          const ca = Math.cos(ang), sa = Math.sin(ang);
          for (const layer of [{cx:rx, al:a*0.7}, {cx:bx, al:a}]){
            layer.cx.strokeStyle = `rgba(255,255,255,${layer.al})`;
            layer.cx.lineWidth = 1; layer.cx.lineCap = 'butt';
            for (const ox of [-TEXW,0,TEXW]){
              layer.cx.beginPath(); layer.cx.moveTo(x0+ox, y0);
              layer.cx.quadraticCurveTo(x0+ox + ca*len/2, y0 + sa*len/2 + (rnd()-0.5)*8,
                                        x0+ox + ca*len, y0 + sa*len);
              layer.cx.stroke();
            }
          }
        }
        out.roughnessMap = rough; out.absoluteRoughness = true;
        out.bumpMap = bump; out.bumpScale = 0.0075 * wav;
      }

      if (t.kind === 'patina'){
        const c = makeCanvas(), x = c.getContext('2d');
        x.fillStyle = '#808080'; x.fillRect(0,0,TEXW,TEXH);
        for (let i=0;i<110;i++){
          const r = 25 + rnd()*90, a = (0.02 + rnd()*0.06) * S * 2;
          wrapDot(x, rnd()*TEXW, rnd()*TEXH, r, (rnd()>0.45?`rgba(0,0,0,${a})`:`rgba(255,255,255,${a*0.8})`));
        }
        const grain = t.grain ?? 0;            // fine speckle grain (dots survive mipmapping,
        if (grain > 0){                        // unlike per-pixel noise)
          const nDots = Math.round(24000 * grain);
          for (let i=0;i<nDots;i++){
            const r = 0.6 + rnd()*1.7, a = 0.05 + rnd()*0.13;
            x.fillStyle = rnd() > 0.5 ? `rgba(255,255,255,${a})` : `rgba(0,0,0,${a})`;
            x.beginPath(); x.arc(rnd()*TEXW, rnd()*TEXH, r, 0, 7); x.fill();
          }
          out.bumpMap = c; out.bumpScale = 0.0035 * grain;
        }
        out.roughnessMap = c;
        // color mottle
        const cc = makeCanvas(), cx = cc.getContext('2d');
        cx.fillStyle = '#ffffff'; cx.fillRect(0,0,TEXW,TEXH);
        const tint = t.tint || '#8a7a4e';
        for (let i=0;i<70;i++){
          const r = 30 + rnd()*110, a = (0.015 + rnd()*0.05) * S * 2;
          cx.globalAlpha = 1;
          const col = rnd() > 0.35 ? tint : '#ffffff';
          wrapDot(cx, rnd()*TEXW, rnd()*TEXH, r, hexA(col, a));
        }
        out.map = cc;
      }

      if (t.kind === 'speckle'){
        // bump-only: keeps material.roughness at its full uncapped value (matte finishes)
        // while the normal jitter still makes rotation perceptible.
        const c = makeCanvas(), x = c.getContext('2d');
        x.fillStyle = '#808080'; x.fillRect(0,0,TEXW,TEXH);
        const img = x.getImageData(0,0,TEXW,TEXH), d = img.data;
        for (let i=0;i<d.length;i+=4){
          const v = 128 + (rnd()-0.5) * 46 * S * 2;
          d[i]=d[i+1]=d[i+2]=v;
        }
        x.putImageData(img,0,0);
        // a few soft clouds
        for (let i=0;i<24;i++){
          const r = 40+rnd()*120, a = (0.02+rnd()*0.04)*S*2;
          wrapDot(x, rnd()*TEXW, rnd()*TEXH, r, (rnd()>0.5?`rgba(0,0,0,${a})`:`rgba(255,255,255,${a})`));
        }
        out.bumpMap = c; out.bumpScale = 0.002 * S;
      }

      if (t.kind === 'smudge'){
        const c = makeCanvas(), x = c.getContext('2d');
        x.fillStyle = '#808080'; x.fillRect(0,0,TEXW,TEXH);
        for (let i=0;i<7;i++){
          const r = 70 + rnd()*150, a = (0.05 + rnd()*0.08) * S * 2;
          wrapDot(x, rnd()*TEXW, rnd()*TEXH, r, `rgba(255,255,255,${a})`);
        }
        for (let i=0;i<26;i++){ // hairlines
          const y0 = rnd()*TEXH, x0 = rnd()*TEXW, len = 30+rnd()*120;
          x.strokeStyle = `rgba(255,255,255,${(0.02+rnd()*0.05)*S*2})`; x.lineWidth = 1;
          for (const ox of [-TEXW,0,TEXW]){
            x.beginPath(); x.moveTo(x0+ox,y0);
            x.quadraticCurveTo(x0+ox+len/2, y0+(rnd()-0.5)*10, x0+ox+len, y0+(rnd()-0.5)*4);
            x.stroke();
          }
        }
        out.roughnessMap = c;
      }
      return out;
    }
    function hexA(hex, a){
      const n = parseInt(hex.slice(1),16);
      return `rgba(${(n>>16)&255},${(n>>8)&255},${n&255},${a})`;
    }

    function toTexture(cnv, srgb){
      const tex = new T.CanvasTexture(cnv);
      tex.wrapS = T.RepeatWrapping; tex.wrapT = T.ClampToEdgeWrapping;
      tex.anisotropy = renderer.capabilities.getMaxAnisotropy();
      if (srgb) tex.colorSpace = T.SRGBColorSpace;
      return tex;
    }

    function buildMaterial(p){
      if (p.glass){
        // Physically-based glass: transmission samples a refraction pass of the
        // ball's own scene (see makeGlassExtras for the backdrop it refracts).
        const gm = new T.MeshPhysicalMaterial({
          color: new T.Color(p.color), metalness: 0, roughness: p.roughness,
          transmission: 1, ior: p.ior, thickness: p.thickness,
          attenuationColor: new T.Color(p.attenuationColor), attenuationDistance: p.attenuationDistance,
          dispersion: p.dispersion || 0, specularIntensity: 1,
          envMap: getEnv(p.env || 'studio'), envMapIntensity: p.envInt
        });
        // three.js uses one constant thickness for transmission, so the longer
        // light path at a sphere's edge (the dark sea-glass rim in the mock) is
        // added here: tint toward rimColor as the surface turns away from view.
        const rimCol = new T.Color(p.rimColor || '#5f7772');
        gm.onBeforeCompile = (sh) => {
          sh.uniforms.uRimCol = { value: rimCol };
          sh.uniforms.uRim = { value: new T.Vector2(p.rimStart ?? 0.55, p.rimAmount ?? 0.85) };
          sh.fragmentShader = sh.fragmentShader
            .replace('void main() {', 'uniform vec3 uRimCol; uniform vec2 uRim;\nvoid main() {')
            .replace('#include <opaque_fragment>',
              `float ndv = saturate(dot(geometryNormal, geometryViewDir));
               // projected radius: linear across the screen, so the tint ramps over many
               // pixels instead of piling up in the outermost ring (which aliased into steps)
               float rr = sqrt(1.0 - ndv * ndv);
               float rimK = smoothstep(uRim.x, 1.0, rr);
               outgoingLight = mix(outgoingLight, outgoingLight * uRimCol * 1.6, rimK * uRim.y);
               #include <opaque_fragment>`);
        };
        gm.customProgramCacheKey = () => 'glass-rim';
        return gm;
      }
      const maps = makeDetailTextures(p);
      const mat = new T.MeshPhysicalMaterial({
        color: new T.Color(p.color),
        metalness: p.metalness,
        roughness: p.roughness,
        envMap: getEnv(p.env || 'studio'),
        envMapIntensity: p.envInt,
        clearcoat: p.clearcoat || 0,
        clearcoatRoughness: p.clearcoatRoughness || 0
      });
      if (p.anisotropy){ mat.anisotropy = p.anisotropy; mat.anisotropyRotation = p.anisotropyRotation || 0; }
      if (maps.map) mat.map = toTexture(maps.map, true);
      if (maps.roughnessMap){
        mat.roughnessMap = toTexture(maps.roughnessMap, false);
        // 'polish' encodes absolute roughness in the map; other kinds modulate
        // around mid-grey (roughness*2*mapValue → mid grey ≈ p.roughness)
        mat.roughness = maps.absoluteRoughness ? 1 : Math.min(1, p.roughness * 2);
      }
      if (maps.bumpMap){ mat.bumpMap = toTexture(maps.bumpMap, false); mat.bumpScale = maps.bumpScale; }
      return mat;
    }

    /* ---------------- Per-ball scenes ----------------
       (The ground shadow lives in the DOM: a sphere's shadow is identical at
        every rotation, so a baked soft ellipse under each stage is exact.) */
    /* Default spin axis: tilted ~30° from vertical (leaning screen-left, a touch
       toward the viewer). Idle rotation turns around this axis, and after a drag
       the ball eases back to it. */
    const Y_AXIS = new T.Vector3(0, 1, 0);
    const TILT = new T.Quaternion().setFromEuler(new T.Euler(0.10, 0, -0.50));  // ≈30°

    const sphereGeo = new T.SphereGeometry(1, 96, 64);

    /* Glass needs something to refract. Over a transparent canvas three.js would
       refract a flat 50% white, so each glass ball gets a backdrop plane that is
       drawn ONLY in the refraction pass (colorWrite toggled per pass) — tinted
       from the page's real computed background, so it stays correct wherever the
       block is embedded. It's invisible in the final image; the DOM shadow
       underneath still shows. */
    function makeGlassExtras(p, scene, mesh){
      const fades = [];   // materials that follow the cell's scroll-reveal fade
      // page background as sRGB bytes (computed style is always rgb()/rgba())
      const m = (getComputedStyle(section).backgroundColor || '').match(/[\d.]+/g) || [244,241,236];
      const [br, bgc, bb] = m.slice(0, 3).map(Number);
      const c = document.createElement('canvas'); c.width = 4; c.height = 256;
      const x = c.getContext('2d');
      const g = x.createLinearGradient(0,0,0,256);          // page above, a soft "floor" below
      const cssS = (k) => `rgb(${Math.round(br*k)},${Math.round(bgc*k)},${Math.round(bb*k)})`;
      g.addColorStop(0, cssS(1)); g.addColorStop(0.55, cssS(1)); g.addColorStop(0.72, cssS(p.backdropShade ?? 0.94)); g.addColorStop(1, cssS((p.backdropShade ?? 0.94) - 0.04));
      x.fillStyle = g; x.fillRect(0,0,4,256);
      const tex = new T.CanvasTexture(c); tex.colorSpace = T.SRGBColorSpace;
      const bm = new T.MeshBasicMaterial({ map: tex, toneMapped: false, depthWrite: false });
      bm.color.setScalar(p.backdropGain ?? 1);   // pre-compensates the tone-mapping of the refracted page
      const back = new T.Mesh(new T.PlaneGeometry(14, 14), bm);
      back.position.set(0, 0, -3.2);
      back.renderOrder = -1;
      back.onBeforeRender = (r) => { bm.colorWrite = r.getRenderTarget() !== null; };
      scene.add(back);

      // seed bubbles: tiny mirror spheres inside the glass — refracted, they read
      // as the little air bubbles of real glass, and they make the spin visible
      const bubbleMat = new T.MeshStandardMaterial({ color: 0xffffff, metalness: 1, roughness: 0.08,
                                                     envMap: getEnv(p.env), envMapIntensity: 1.1 });
      const bubbleGeo = new T.SphereGeometry(1, 16, 12);
      let sd = 7;  const r2 = () => (sd = (sd * 16807) % 2147483647) / 2147483647;
      // (bubbles stay opaque even while the ball fades in: transparent ones
      //  would drop out of the refraction pass and vanish behind the glass)
      for (let i = 0; i < (p.bubbles || 0); i++){
        const rad = 0.005 + Math.pow(r2(), 3) * 0.016;
        const d = Math.cbrt(r2()) * 0.82, th = r2() * Math.PI * 2, ph = Math.acos(2 * r2() - 1);
        const bub = new T.Mesh(bubbleGeo, bubbleMat);
        bub.scale.setScalar(rad);
        bub.position.set(d * Math.sin(ph) * Math.cos(th), d * Math.cos(ph), d * Math.sin(ph) * Math.sin(th));
        mesh.add(bub);
      }

      // internal glow lower-right: light focused through the ball onto its far
      // side (fixed to the light, so it stays put while the ball turns)
      if (p.glow){
        const gc = document.createElement('canvas'); gc.width = gc.height = 128;
        const gx = gc.getContext('2d'), gg = gx.createRadialGradient(64,64,0,64,64,64);
        gg.addColorStop(0,'rgba(255,255,250,0.85)'); gg.addColorStop(0.45,'rgba(255,255,250,0.5)'); gg.addColorStop(1,'rgba(255,255,250,0)');
        gx.fillStyle = gg; gx.fillRect(0,0,128,128);
        const spr = new T.Sprite(new T.SpriteMaterial({ map: new T.CanvasTexture(gc), blending: T.AdditiveBlending,
                                   depthTest: false, transparent: true, opacity: 0, toneMapped: false }));
        // starts at 0 like the ball; the render loop brings it up to p.glow with the fade
        spr.position.set(0.40, -0.40, 0.80); spr.scale.setScalar(0.62);
        scene.add(spr);
        fades.push({ mat: spr.material, base: p.glow });
      }
      return { fades, envMats: [bubbleMat] };
    }

    const balls = [];

    for (const item of section.querySelectorAll('.finish-swatch[data-finish]')){
      const key = item.dataset.finish;
      const p = FINISHES[key];
      const stage = item.querySelector('.finish-swatch-media');
      if (!p || !stage) continue;

      const scene = new T.Scene();
      const cam = new T.PerspectiveCamera(33, 1, 0.1, 50);
      cam.position.set(0, 0.3, 4.7);
      cam.lookAt(0, -0.06, 0);

      const mat = buildMaterial(p);
      // transparent + start invisible so the ball can only appear by
      // following its cell's scroll-reveal fade (no first-frame flash)
      mat.transparent = true;
      mat.opacity = 0;
      const mesh = new T.Mesh(sphereGeo, mat);
      // default pose: tilted axis, each ball at a different heading — unless
      // the finish fixes its own (paintable: its pour is drawn to face the camera)
      const theta0 = p.heading !== undefined ? p.heading : balls.length * 1.7;
      mesh.quaternion.setFromAxisAngle(Y_AXIS, theta0).premultiply(TILT);
      scene.add(mesh);
      const extras = p.glass ? makeGlassExtras(p, scene, mesh) : { fades: [], envMats: [] };

      balls.push({ key, stage, item, scene, cam, mesh, mat, theta: theta0,
        fades: extras.fades, envMats: extras.envMats,
        vel:new T.Vector2(0,0), dragging:false, lastPointer:null, history:[],
        lastInteract:-1e9, idleW:0, lastOpacity:0 });
    }

    if (!balls.length) return;

    /* ---------------- Interaction: drag / swipe / keys, with inertia ---------------- */
    const ROT = 4.6;                 // radians per full stage-width drag
    const tmpQ = new T.Quaternion(), tmpA = new T.Vector3();

    function spinWorld(b, ax, ay, angle){
      tmpA.set(ax, ay, 0).normalize();
      tmpQ.setFromAxisAngle(tmpA, angle);
      b.mesh.quaternion.premultiply(tmpQ);
    }

    for (const b of balls){
      const el = b.stage;
      el.addEventListener('pointerdown', (e) => {
        el.setPointerCapture(e.pointerId);
        b.dragging = true; b.vel.set(0,0);
        b.lastPointer = { x:e.clientX, y:e.clientY, t:performance.now() };
        b.history.length = 0;
        b.lastInteract = performance.now();
      });
      el.addEventListener('pointermove', (e) => {
        if (!b.dragging || !b.lastPointer) return;
        const r = el.getBoundingClientRect();
        const now = performance.now();
        const dx = (e.clientX - b.lastPointer.x) / r.width;
        const dy = (e.clientY - b.lastPointer.y) / r.width;
        spinWorld(b, dy * ROT, dx * ROT, Math.hypot(dx, dy) * ROT);
        b.history.push({ dx, dy, t: now });
        while (b.history.length && now - b.history[0].t > 110) b.history.shift();
        b.lastPointer = { x:e.clientX, y:e.clientY, t: now };
        b.lastInteract = now;
        needsFrame = true;
      });
      const release = () => {
        if (!b.dragging) return;
        b.dragging = false; b.lastPointer = null;
        if (!reducedMotion && b.history.length > 1){
          const now = performance.now();
          let sx = 0, sy = 0; const t0 = b.history[0].t;
          for (const h of b.history){ sx += h.dx; sy += h.dy; }
          const dt = Math.max((now - t0) / 1000, 0.016);
          b.vel.set((sy / dt) * ROT, (sx / dt) * ROT);   // (x-axis rate, y-axis rate)
          const max = 14; if (b.vel.length() > max) b.vel.setLength(max);
        }
        b.lastInteract = performance.now();
      };
      el.addEventListener('pointerup', release);
      el.addEventListener('pointercancel', release);
      el.addEventListener('lostpointercapture', release);
      el.addEventListener('keydown', (e) => {
        const imp = 2.6;
        let used = true;
        if (e.key === 'ArrowLeft')  b.vel.y -= imp;
        else if (e.key === 'ArrowRight') b.vel.y += imp;
        else if (e.key === 'ArrowUp') b.vel.x -= imp;
        else if (e.key === 'ArrowDown') b.vel.x += imp;
        else used = false;
        if (used){ e.preventDefault(); b.lastInteract = performance.now(); needsFrame = true; }
      });
    }

    /* ---------------- Layout & render loop ---------------- */
    let cssW = 0, cssH = 0, needsFrame = true, visible = true, frames = 0;

    function resize(){
      const r = section.getBoundingClientRect();
      cssW = Math.max(1, Math.round(r.width));
      cssH = Math.max(1, Math.round(r.height));
      renderer.setPixelRatio(DPR());
      renderer.setSize(cssW, cssH, false);
      // re-run the warm-up so a few frames repaint while breakpoint
      // layout (fonts, media queries) settles into place
      frames = 0;
      needsFrame = true;
    }
    new ResizeObserver(resize).observe(section);
    window.addEventListener('resize', resize);
    window.addEventListener('orientationchange', resize);
    resize();

    /* Viewport/DPR switches (e.g. responsive mode) can drop the WebGL
       context, which would otherwise leave the canvas permanently blank */
    canvas.addEventListener('webglcontextlost', (e) => { e.preventDefault(); });
    canvas.addEventListener('webglcontextrestored', () => {
      // the PMREM environments live in render targets, which do not
      // survive a context restore — rebuild them and rebind the materials
      ENV_CACHE = {};
      for (const b of balls){
        const env = getEnv(FINISHES[b.key].env || 'studio');
        b.mat.envMap = env;
        b.mat.needsUpdate = true;
        for (const m of b.envMats){ m.envMap = env; m.needsUpdate = true; }
      }
      resize();
    });

    if ('IntersectionObserver' in window){
      new IntersectionObserver((es) => { visible = es[0].isIntersecting; if (visible) needsFrame = true; })
        .observe(section);
    }

    const IDLE_DELAY = 2600, IDLE_SPEED = 0.28;
    let lastT = performance.now();

    function tick(now){
      requestAnimationFrame(tick);
      const dt = Math.min((now - lastT) / 1000, 0.05);
      lastT = now;
      if (!visible) return;
      if (frames < 6) needsFrame = true;   // warm-up frames

      let animating = false;
      for (const b of balls){
        // inertia
        if (!b.dragging && b.vel.lengthSq() > 1e-5){
          spinWorld(b, b.vel.x, b.vel.y, b.vel.length() * dt);
          b.vel.multiplyScalar(Math.exp(-dt * 2.4));
          if (b.vel.lengthSq() < 1e-5) b.vel.set(0,0);
          animating = true;
        }
        // idle: ease back to the tilted default axis, then keep turning around it
        if (!reducedMotion){
          const idleTarget = (!b.dragging && now - b.lastInteract > IDLE_DELAY && b.vel.lengthSq() < 1e-5) ? 1 : 0;
          if (idleTarget === 1 && b.idleW <= 0.002){
            // re-engaging after a drag: resume from the yaw closest to where the
            // user left the ball, so the ease back to the default axis is short
            tmpQ.copy(TILT).invert().multiply(b.mesh.quaternion);
            b.theta = 2 * Math.atan2(tmpQ.y, tmpQ.w);
          }
          b.idleW += (idleTarget - b.idleW) * Math.min(1, dt * 2.2);
          if (b.idleW > 0.002){
            b.theta += IDLE_SPEED * b.idleW * dt;
            tmpQ.setFromAxisAngle(Y_AXIS, b.theta).premultiply(TILT);   // TILT ∘ yaw
            b.mesh.quaternion.slerp(tmpQ, Math.min(1, dt * 2.0));       // reset to default axis
            animating = true;
          }
        }
        if (b.dragging) animating = true;
        // mirror the DOM scroll-reveal fade (the canvas isn't affected by
        // the cell's opacity, so the ball follows it explicitly)
        const o = parseFloat(getComputedStyle(b.item).opacity);
        if (o !== b.lastOpacity){
          b.mat.opacity = o;
          for (const f of b.fades) f.mat.opacity = f.base * o;
          b.lastOpacity = o; animating = true;
        }
        if (o < 1) animating = true;
      }

      if (!animating && !needsFrame) return;
      needsFrame = false;

      renderer.setScissorTest(false);
      renderer.setViewport(0, 0, cssW, cssH);
      renderer.clear(true, true, false);
      renderer.setScissorTest(true);

      const sr = section.getBoundingClientRect();
      for (const b of balls){
        const r = b.stage.getBoundingClientRect();
        const x = r.left - sr.left;
        const y = cssH - (r.top - sr.top) - r.height;
        renderer.setViewport(x, y, r.width, r.height);
        renderer.setScissor(x, y, r.width, r.height);
        b.cam.aspect = r.width / r.height;
        b.cam.updateProjectionMatrix();
        renderer.render(b.scene, b.cam);
      }

      frames++;
    }
    requestAnimationFrame(tick);
    section.classList.add('gl-on');

    // safety: if fonts load late, rects shift — repaint on font ready
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(() => { needsFrame = true; });
  }

  // Flat-mode sections render pre-rendered stills and carry no canvas.
  // Each section is only built as it nears the viewport: building the balls'
  // materials (the paintable pour texture above all) is heavy main-thread
  // work, and doing it on load for a section further down held up the page.
  // Until then the static gradient swatches show, as they do without WebGL.
  const INIT_MARGIN = '600px 0px';
  for (const section of document.querySelectorAll('.finish-comparison-section:not(.finish-comparison-flat)')){
    if (!('IntersectionObserver' in window)){ initSection(section); continue; }
    const io = new IntersectionObserver((entries) => {
      if (!entries.some((e) => e.isIntersecting)) return;
      io.disconnect();
      initSection(section);
    }, { rootMargin: INIT_MARGIN });
    io.observe(section);
  }
})();
